import { Sparkles, Trophy, TrendingUp, Target, Boxes } from "lucide-react";

/**
 * 1080×1080 Instagram-ready archetype share card.
 * Rendered off-screen and exported to PNG via html-to-image.
 * Self-contained (no API calls) so it captures cleanly.
 */
export default function ArchetypeShareCard({ archetype, stats, owner, innerRef }) {
  const slug = owner?.profile_slug ? `@${owner.profile_slug}` : owner?.name || "Castfolio Collector";
  const displayName = owner?.name || owner?.username || "Castfolio Collector";

  return (
    <div
      ref={innerRef}
      data-testid="archetype-share-card"
      style={{
        width: 1080,
        height: 1080,
        background:
          "radial-gradient(circle at 80% 0%, rgba(244,114,182,0.35) 0%, transparent 55%), radial-gradient(circle at 0% 100%, rgba(251,191,36,0.18) 0%, transparent 50%), linear-gradient(140deg, #06060a 0%, #0f0817 50%, #2a0b3e 100%)",
        color: "#fff",
        fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
        position: "relative",
        overflow: "hidden",
        padding: "72px 84px",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
      }}
    >
      {/* Subtle grid texture */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
          pointerEvents: "none",
        }}
      />
      {/* Glow blob */}
      <div
        style={{
          position: "absolute",
          top: -180,
          right: -180,
          width: 540,
          height: 540,
          borderRadius: "50%",
          background: "rgba(232,121,249,0.22)",
          filter: "blur(120px)",
          pointerEvents: "none",
        }}
      />

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", position: "relative", zIndex: 2 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: "linear-gradient(135deg,#f43f5e,#a855f7)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 900,
              fontSize: 22,
              letterSpacing: -1,
            }}
          >
            C
          </div>
          <div>
            <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: -0.4 }}>Castfolio</div>
            <div style={{ fontSize: 13, color: "#a1a1aa", letterSpacing: 2, textTransform: "uppercase" }}>Your diecast portfolio</div>
          </div>
        </div>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "10px 18px",
            borderRadius: 999,
            background: "rgba(251,191,36,0.12)",
            border: "1px solid rgba(251,191,36,0.4)",
            color: "#fde68a",
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: 3,
            textTransform: "uppercase",
          }}
        >
          <Sparkles style={{ width: 14, height: 14 }} /> Collector Archetype
        </div>
      </div>

      {/* Big archetype */}
      <div style={{ marginTop: 110, position: "relative", zIndex: 2 }}>
        <p style={{ color: "#fbbf24", fontSize: 16, fontWeight: 700, letterSpacing: 4, textTransform: "uppercase", margin: 0 }}>
          You are
        </p>
        <h1
          style={{
            fontSize: 116,
            fontWeight: 900,
            lineHeight: 1.02,
            letterSpacing: -3,
            margin: "16px 0 28px",
            background: "linear-gradient(135deg,#fff 0%,#fbcfe8 60%,#f0abfc 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          {archetype?.name || "The Newcomer"}
        </h1>
        <p style={{ fontSize: 28, lineHeight: 1.4, color: "#e4e4e7", maxWidth: 880, margin: 0, fontWeight: 400 }}>
          {archetype?.tagline || "Your collecting story starts now."}
        </p>
      </div>

      {/* Spacer */}
      <div style={{ flex: 1 }} />

      {/* Stats strip */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 18,
          paddingTop: 36,
          borderTop: "1px solid rgba(255,255,255,0.1)",
          position: "relative",
          zIndex: 2,
        }}
      >
        <ShareStat icon={Boxes} label="Pieces" value={(stats?.pieces ?? 0).toLocaleString()} accent="#fff" />
        <ShareStat icon={Sparkles} label="Brands" value={stats?.brand_diversity ?? 0} accent="#c4b5fd" />
        <ShareStat icon={Trophy} label="Chases" value={stats?.chase_count ?? 0} accent="#fbbf24" />
        <ShareStat icon={TrendingUp} label="Grails" value={stats?.grail_count ?? 0} accent="#fda4af" />
      </div>

      {/* Footer: owner identity */}
      <div
        style={{
          marginTop: 44,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "relative",
          zIndex: 2,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
          {owner?.profile_photo ? (
            <img
              src={owner.profile_photo}
              alt=""
              crossOrigin="anonymous"
              style={{ width: 64, height: 64, borderRadius: "50%", objectFit: "cover", border: "2px solid rgba(255,255,255,0.2)" }}
            />
          ) : (
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                background: "linear-gradient(135deg,#3f3f46,#18181b)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 26,
                fontWeight: 800,
                color: "#a1a1aa",
                border: "2px solid rgba(255,255,255,0.15)",
              }}
            >
              {displayName.charAt(0).toUpperCase()}
            </div>
          )}
          <div>
            <div style={{ fontSize: 22, fontWeight: 800 }}>{displayName}</div>
            <div style={{ fontSize: 16, color: "#a1a1aa", marginTop: 2 }}>{slug}</div>
          </div>
        </div>
        {stats?.top_brand && (
          <div
            style={{
              padding: "12px 22px",
              borderRadius: 14,
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.1)",
              textAlign: "right",
            }}
          >
            <div style={{ fontSize: 11, color: "#71717a", letterSpacing: 2, textTransform: "uppercase" }}>Top brand</div>
            <div style={{ fontSize: 20, fontWeight: 800, marginTop: 4 }}>{stats.top_brand}</div>
          </div>
        )}
      </div>
    </div>
  );
}

function ShareStat({ icon: Icon, label, value, accent }) {
  return (
    <div>
      <Icon style={{ width: 22, height: 22, color: "#71717a", marginBottom: 8 }} />
      <div style={{ fontSize: 56, fontWeight: 900, color: accent, lineHeight: 1, letterSpacing: -1 }}>{value}</div>
      <div style={{ fontSize: 12, color: "#71717a", letterSpacing: 3, textTransform: "uppercase", marginTop: 8 }}>{label}</div>
    </div>
  );
}
